import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Miscellanious } from '@ops/global-config';
import {
  BankAccountService,
  CompanyService,
  LocalStorageService,
  MonthlyBalance,
  MonthlyOverview,
  YearlyOverviewMonth,
} from '@ops/shared-types';
import { ReplaySubject, Subject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-monthly-overview',
  templateUrl: './monthly-overview.component.html',
  styleUrls: ['./monthly-overview.component.scss'],

})
export class MonthlyOverviewComponent implements OnInit, OnDestroy {
  @Input() monthlyOverviewData: {
    monthlyBalance: MonthlyBalance[];
    monthlyOverview: MonthlyOverview;
  };

  @Input('companyId') set companyIdSetter(companyId: string) {
    // console.log('companyIdSetter', companyId);
    this.companyId = companyId;
    this.companyCurrencyCode =
      this.localStorageService.getCurrentCompany()?.baseCurrencyCode;
    this.companyIdChanges$.next(companyId);
  }

  today: Date;

  companyCurrencyCode?: string;

  companyId: string;

  receivedIncome: number;
  paidExpense: number;
  expectedIncome: number;
  expectedExpense: number;
  profitability: number;
  favoriteAccountsBalances: YearlyOverviewMonth['favoriteAccountsBalances'];
  isRefreshing: boolean = false;

  private destroy$ = new Subject<void>();
  private companyIdChanges$ = new ReplaySubject<string>(1);
  private refreshSubscription?: Subscription;

  constructor(
    private localStorageService: LocalStorageService,
    private companyService: CompanyService,
    private changeDetectorRef: ChangeDetectorRef,
    private bankAccountService: BankAccountService
  ) {
  }

  ngOnInit() {
    this.today = new Date();
    this.companyIdChanges$
      .pipe(
        filter((id) => !!id),
        distinctUntilChanged(),
        switchMap((id) =>
          this.companyService.getYearlyOverview(
            id,
            (this.today ?? new Date()).getFullYear()
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((yearlyOverview: Array<YearlyOverviewMonth>) => {
        const todayDate = this.today ?? new Date();
        const relevantData = yearlyOverview?.[todayDate.getMonth()];
        this.receivedIncome = relevantData?.receivedProjectIncome || 0;
        this.paidExpense =
          (relevantData?.paidCompanyExpense || 0) +
          (relevantData?.paidProjectCost || 0);
        this.expectedIncome = relevantData?.expectedProjectIncome || 0;
        this.expectedExpense =
          (relevantData?.expectedCompanyExpense || 0) +
          (relevantData?.expectedProjectCost || 0);
        this.profitability =
          relevantData?.profitability || 0;
        this.favoriteAccountsBalances = relevantData?.favoriteAccountsBalances || [];

        // console.log(relevantData);
        // Ensure view updates under OnPush change detection
        this.changeDetectorRef.markForCheck();
      });
  }


  fetchYearlyOverview() {
    if (!this.companyId) {
      return;
    }
    console.log('refreshed yearly overview');
    this.companyIdChanges$.next(this.companyId);
  }

  refreshData(): void {
    // Prevent multiple simultaneous refresh calls
    if (this.isRefreshing || !this.companyId) {
      return;
    }
    
    // Unsubscribe from any existing refresh subscription to prevent leaks
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
    
    // Set loading state and force immediate synchronous change detection
    this.isRefreshing = true;
    this.changeDetectorRef.detectChanges();
    
    // Start async operation immediately - HTTP requests are non-blocking
    this.refreshSubscription = this.bankAccountService.calculateAllBankAccountHistoriesForCompany(
      this.companyId, 
      null, 
      new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1), 
      new Date(new Date().getFullYear(), new Date().getMonth(), 0)
    ).pipe(
      switchMap(() => 
        this.companyService.getYearlyOverview(
          this.companyId,
          (this.today ?? new Date()).getFullYear()
        )
      ),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (yearlyOverview: Array<YearlyOverviewMonth>) => {
        const todayDate = this.today ?? new Date();
        const relevantData = yearlyOverview?.[todayDate.getMonth()];
        this.receivedIncome = relevantData?.receivedProjectIncome || 0;
        this.paidExpense =
          (relevantData?.paidCompanyExpense || 0) +
          (relevantData?.paidProjectCost || 0);
        this.expectedIncome = relevantData?.expectedProjectIncome || 0;
        this.expectedExpense =
          (relevantData?.expectedCompanyExpense || 0) +
          (relevantData?.expectedProjectCost || 0);
        this.profitability =
          relevantData?.profitability || 0;
        this.favoriteAccountsBalances = relevantData?.favoriteAccountsBalances || [];

        // Clear loading state
        this.isRefreshing = false;
        // Ensure view updates under OnPush change detection
        this.changeDetectorRef.markForCheck();
      },
      error: () => {
        // Clear loading state on error
        this.isRefreshing = false;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  get companyBaseCurrency() {
    return Miscellanious.currencySymbolFromCode(
      'en-US',
      this.companyCurrencyCode
    );
  }

  symbolFor(currencyCode?: string) {
    return Miscellanious.currencySymbolFromCode('en-US', currencyCode || this.companyCurrencyCode);
  }

  ngOnDestroy() {
    // Clean up refresh subscription
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
